/**
 * Aba Trabalhadores da ficha da frente (#365, D5 e D20): os vínculos com entrada e saída, os
 * avisos de cada trabalhador e as ações de vincular, registrar saída, corrigir e apagar (só
 * vínculo ainda aberto). Sem funcionário elegível é pré-requisito ausente: o bloco leva a
 * Funcionários e a Setores, onde se liga um setor à obra.
 */
import React, { useState } from 'react';
import { useSnackbar } from 'notistack';
import { Alert, Box, Chip, Typography } from '@mui/material';
import { Add as AddIcon, WarningAmber as WarnIcon } from '@mui/icons-material';

import { DataTable, type DataTableColumn } from '../../../components';
import { ConfirmDialog } from '../../../components/common/ConfirmDialog';
import { PrerequisiteBlock } from '../../../components/common/PrerequisiteBlock';
import { workFrontService } from '../../../services/workFrontService';
import type {
  WorkFrontDetail,
  WorkFrontEligibleEmployee,
  WorkFrontWorkerItem,
  WorkFrontWorkerWarning,
} from '../../../types/workFront';
import { useAcao } from '../../../authorization/useVeredito';
import { LockableActionButton } from '../../fire-equipments/components/LockableActionButton';
import { formatIsoDate, todayIso } from '../../fire-equipments/fireDates';
import { FRONT_CLOSED_REASON } from './StagesTab';
import WorkerDialog from './WorkerDialog';

const TOUCH_TARGET = 44;

const ALL_LINKED_REASON = 'Todos os funcionários elegíveis já estão vinculados';

const WARNING_TEXT: Record<WorkFrontWorkerWarning, string> = {
  SEM_RISCO: 'Função sem risco aplicado',
  FUNCIONARIO_INATIVO: 'Funcionário inativo',
  FUNCIONARIO_DEMITIDO: 'Funcionário demitido',
  FUNCIONARIO_AFASTADO: 'Funcionário afastado',
  FUNCIONARIO_SUSPENSO: 'Funcionário suspenso',
  FUNCIONARIO_LICENCA: 'Funcionário em licença',
};

const WarningChips: React.FC<{ warnings: WorkFrontWorkerWarning[] }> = ({ warnings }) =>
  warnings.length === 0 ? (
    <>—</>
  ) : (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
      {warnings.map((warning) => (
        <Chip
          key={warning}
          size="small"
          variant="outlined"
          icon={<WarnIcon />}
          label={WARNING_TEXT[warning]}
          sx={(theme) => ({
            color: theme.palette.warning.dark,
            borderColor: theme.palette.warning.dark,
            '& .MuiChip-icon': { color: 'inherit' },
            ...theme.applyStyles('dark', {
              color: theme.palette.warning.light,
              borderColor: theme.palette.warning.light,
            }),
          })}
        />
      ))}
    </Box>
  );

export interface WorkersTabProps {
  front: WorkFrontDetail;
  workers: WorkFrontWorkerItem[];
  eligible: WorkFrontEligibleEmployee[];
  closed: boolean;
  onChanged: () => Promise<void>;
}

export const WorkersTab: React.FC<WorkersTabProps> = ({ front, workers, eligible, closed, onChanged }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [dialogOpen, setDialogOpen] = useState(false);
  // Fica até o diálogo terminar de fechar, para o título não piscar "Vincular trabalhador".
  const [editing, setEditing] = useState<WorkFrontWorkerItem | undefined>(undefined);
  const [removing, setRemoving] = useState<WorkFrontWorkerItem | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);

  const lockedReason = closed ? FRONT_CLOSED_REASON : undefined;
  const linkedIds = new Set(workers.filter((worker) => !worker.exitDate).map((worker) => worker.employee.id));
  const available = eligible.filter((employee) => !linkedIds.has(employee.id));
  const noEligible = eligible.length === 0;
  const allLinked = !noEligible && available.length === 0;
  const cannotUpdate = useAcao('work_front.update', 'Vincular trabalhador').disabled;

  const openDialog = (worker?: WorkFrontWorkerItem) => {
    setEditing(worker);
    setDialogOpen(true);
  };

  const registerExit = async (worker: WorkFrontWorkerItem) => {
    setBusy(true);
    setError(null);
    try {
      await workFrontService.updateWorker(front.id, worker.id, { exitDate: todayIso() });
      enqueueSnackbar(`Saída de ${worker.employee.name} registrada hoje.`, { variant: 'success' });
      await onChanged();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Não foi possível registrar a saída.');
    } finally {
      setBusy(false);
    }
  };

  const confirmRemove = async () => {
    if (!removing) return;
    setBusy(true);
    setRemoveError(null);
    try {
      await workFrontService.removeWorker(front.id, removing.id);
      setRemoving(null);
      enqueueSnackbar('Vínculo apagado.', { variant: 'success' });
      await onChanged();
    } catch (err: unknown) {
      setRemoveError(err instanceof Error ? err.message : 'Não foi possível apagar o vínculo.');
    } finally {
      setBusy(false);
    }
  };

  const columns: DataTableColumn<WorkFrontWorkerItem>[] = [
    {
      id: 'name',
      label: 'Trabalhador',
      primary: true,
      render: (worker) => (
        <Box>
          <Typography variant="body1" fontWeight="bold">
            {worker.employee.name}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {[worker.employee.occupation, worker.employee.department].filter(Boolean).join(' · ') || '—'}
          </Typography>
        </Box>
      ),
    },
    { id: 'entry', label: 'Entrada', render: (worker) => formatIsoDate(worker.entryDate) },
    {
      id: 'exit',
      label: 'Saída',
      render: (worker) =>
        worker.exitDate ? (
          <Box>
            <Typography variant="body1">{formatIsoDate(worker.exitDate)}</Typography>
            <Typography variant="body1" color="text.secondary">
              Saiu da frente
            </Typography>
          </Box>
        ) : (
          '—'
        ),
    },
    { id: 'warnings', label: 'Aviso', render: (worker) => <WarningChips warnings={worker.warnings} /> },
    {
      id: 'actions',
      label: 'Ações',
      render: (worker) => (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {!worker.exitDate && (
            <LockableActionButton
              acao="work_front.update"
              rotulo={`Registrar saída de ${worker.employee.name}`}
              variant="outlined"
              lockedReason={lockedReason}
              disabled={busy}
              sx={{ minHeight: TOUCH_TARGET }}
              onClick={() => void registerExit(worker)}
            >
              Registrar saída
            </LockableActionButton>
          )}
          <LockableActionButton
            acao="work_front.update"
            rotulo={`Corrigir vínculo de ${worker.employee.name}`}
            lockedReason={lockedReason}
            disabled={busy}
            sx={{ minHeight: TOUCH_TARGET }}
            onClick={() => openDialog(worker)}
          >
            Corrigir
          </LockableActionButton>
          {!worker.exitDate && (
            <LockableActionButton
              acao="work_front.update"
              rotulo={`Apagar vínculo de ${worker.employee.name}`}
              color="error"
              lockedReason={lockedReason}
              disabled={busy}
              sx={{ minHeight: TOUCH_TARGET }}
              onClick={() => {
                setRemoveError(null);
                setRemoving(worker);
              }}
            >
              Apagar
            </LockableActionButton>
          )}
        </Box>
      ),
    },
  ];

  const noEligibleBlock = (
    <PrerequisiteBlock
      title={`Nenhum funcionário da ${front.unit.name} disponível`}
      reason="Só funcionário ativo da empresa, com setor ligado à obra ou com a obra no cadastro, pode ser vinculado. Cadastre o funcionário ou ligue um setor à obra e volte aqui."
      actions={[
        { route: '/employees', label: 'Ir para Funcionários', requiredPermission: 'employee.read' },
        { route: '/departments', label: 'Ligar um setor à obra', requiredPermission: 'department.update' },
      ]}
    />
  );

  if (noEligible && workers.length === 0) return noEligibleBlock;

  const addAction = {
    label: 'Vincular trabalhador',
    node: 'work-fronts',
    deniedHint: cannotUpdate
      ? 'Peça a quem administra a empresa para vincular trabalhadores.'
      : closed
        ? FRONT_CLOSED_REASON
        : ALL_LINKED_REASON,
    onClick: () => openDialog(),
    allowed: !closed && !cannotUpdate && !allLinked,
  };

  return (
    <Box>
      {noEligible && <Box sx={{ mb: 2 }}>{noEligibleBlock}</Box>}

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 2, mb: 2 }}>
        <LockableActionButton
          acao="work_front.update"
          rotulo="Vincular trabalhador"
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          lockedReason={lockedReason ?? (allLinked ? ALL_LINKED_REASON : undefined)}
          disabled={noEligible}
          sx={{ minHeight: TOUCH_TARGET }}
          onClick={() => openDialog()}
        >
          Vincular trabalhador
        </LockableActionButton>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <DataTable
        embedded
        columns={columns}
        rows={workers}
        getRowId={(worker) => worker.id}
        emptyState={{
          title: 'Nenhum trabalhador vinculado a esta frente',
          description:
            'Vincule os funcionários da obra que trabalham nesta frente, com a data de entrada. Quem sai fica no histórico da frente, com a data de saída.',
          action: addAction,
        }}
      />

      <WorkerDialog
        open={dialogOpen}
        frontId={front.id}
        eligible={available}
        worker={editing}
        onClose={() => setDialogOpen(false)}
        onSaved={() => {
          setDialogOpen(false);
          enqueueSnackbar(editing ? 'Vínculo corrigido.' : 'Trabalhador vinculado.', { variant: 'success' });
          void onChanged();
        }}
      />

      <ConfirmDialog
        open={removing !== null}
        title="Apagar vínculo"
        message={
          removing
            ? `Apagar o vínculo de ${removing.employee.name} com esta frente? Para quem saiu da frente, registre a saída: o histórico fica guardado.`
            : ''
        }
        confirmLabel="Apagar vínculo"
        confirmColor="error"
        loading={busy}
        error={removeError}
        onConfirm={() => void confirmRemove()}
        onCancel={() => setRemoving(null)}
      />
    </Box>
  );
};

export default WorkersTab;
